// ─────────────────────────────────────────────────────────────────────────────
// FaceLayers — the ring primitives for every target face, plus FACE_LAYERS:
// the single `face -> layers` map that drives BOTH the full face canvas
// (`<FaceCanvas>` in components/Target.jsx) and the magnified drag preview
// (`previewRings`). Before #2063 the same geometry was spelled out twice —
// once inline in each of 17 near-identical face components and again in a
// parallel `previewRings` switch — and the two drifted.
//
// A layer descriptor is purely BACKGROUND geometry. It can describe the mat,
// the SVG background colour, decorative art, the rings and the zone-value
// labels. It deliberately cannot reach the arrow overlay: overlay glyphs are
// drawn by DraggableArrows with FIXED styles (see the note there and
// round-detail/svgDots) and must never take a score-derived colour (#1846 →
// #1849). Keeping the descriptor background-only is what enforces that.
//
//   rings      required — the scoring ring geometry, shared by canvas + preview
//   mat        canvas only — draw the square paper/mat rect behind the rings
//   background canvas only — SVG element background colour
//   decor      canvas only — decorative art drawn behind the rings
//   labels     canvas only — zone-value text drawn over the rings
//   previewBg  preview only — background rect for the magnified preview, used
//              by the faces whose canvas gets its backdrop from `background`
//
// All geometry below is in the 240×240 viewbox frame (centre 120,120,
// WA_OUTER_R = 100) — NOT the normalised 1.0-is-the-paper-edge frame the
// `arrows` prop uses. Do not convert between them here.
// ─────────────────────────────────────────────────────────────────────────────
import {
  WA_OUTER_R,
  VEGAS_SPOT_CENTERS,
  VEGAS_SPOT_R,
  TRI_SPOT_CENTERS,
  TRI_SPOT_R,
  NFAA5_SPOT_CENTERS,
  NFAA5_SPOT_R,
  NFAA_SINGLE_OUTER_R,
  WAFIELD3_SPOT_CENTERS,
  WAFIELD3_SPOT_R,
  D3_VITAL,
  D3_OUTER_R,
} from "../../lib/targetGeometry";

// ─────────────────────────────────────────────────────────────────────────────
// Shared ring stroke style
// ─────────────────────────────────────────────────────────────────────────────
const RING_STROKE = { fill: "none", stroke: "var(--nock)", strokeWidth: 0.4 };
const RING_STROKE_LIGHT = { ...RING_STROKE, opacity: 0.4 };
const RING_STROKE_MED   = { ...RING_STROKE, opacity: 0.45 };

// Multi-spot faces have per-spot centres: the geometry comes from the shared
// targetGeometry module so the render and hitZone() can't drift (#1368, #1488).
const SPOT_CENTERS = VEGAS_SPOT_CENTERS;
const TRI_CENTERS = TRI_SPOT_CENTERS;
const NFAA5_CENTERS = NFAA5_SPOT_CENTERS;

// ─────────────────────────────────────────────────────────────────────────────
// Ring primitive functions. Each renders the colour fills, zone dividers, and
// X-ring/centre marks for one face type. Background rects and overlay elements
// (arrows, silhouettes, score labels) are handled by the caller.
// ─────────────────────────────────────────────────────────────────────────────

function WARings({ cx, cy }) {
  return (<>
    <circle cx={cx} cy={cy} r={WA_OUTER_R} fill="var(--vellum)" />
    <circle cx={cx} cy={cy} r="80"  fill="var(--ring-dark)" />
    <circle cx={cx} cy={cy} r="60"  fill="var(--field)" />
    <circle cx={cx} cy={cy} r="40"  fill="var(--cinder)" />
    <circle cx={cx} cy={cy} r="20"  fill="var(--gold)" />
    <circle cx={cx} cy={cy} r="90" {...RING_STROKE_LIGHT} />
    <circle cx={cx} cy={cy} r="70" {...RING_STROKE_MED} />
    <circle cx={cx} cy={cy} r="50" {...RING_STROKE_MED} />
    <circle cx={cx} cy={cy} r="30" {...RING_STROKE_LIGHT} />
    <circle cx={cx} cy={cy} r="10" fill="none" stroke="var(--nock)" strokeWidth="0.4" opacity=".5" />
    <circle cx={cx} cy={cy} r="5"  fill="none" stroke="var(--nock)" strokeWidth="0.4" strokeDasharray="2 1.5" opacity=".5" />
    <line x1={cx-1} y1={cy}   x2={cx+1} y2={cy}   stroke="var(--nock)" strokeWidth="0.4" opacity=".5" />
    <line x1={cx}   y1={cy-1} x2={cx}   y2={cy+1} stroke="var(--nock)" strokeWidth="0.4" opacity=".5" />
  </>);
}

function VegasSpotRings({ cx, cy }) {
  return (<>
    <circle cx={cx} cy={cy} r={VEGAS_SPOT_R} fill="var(--field)" />
    <circle cx={cx} cy={cy} r="24" fill="var(--cinder)" />
    <circle cx={cx} cy={cy} r="12" fill="var(--gold)" />
    {[3,6,12,18,24,30].map(r => (
      <circle key={r} cx={cx} cy={cy} r={r} fill="none" stroke="var(--nock)" strokeWidth="0.4" opacity=".55" />
    ))}
    <line x1={cx-2} y1={cy}   x2={cx+2} y2={cy}   stroke="var(--nock)" strokeWidth=".4" opacity=".55" />
    <line x1={cx}   y1={cy-2} x2={cx}   y2={cy+2} stroke="var(--nock)" strokeWidth=".4" opacity=".55" />
  </>);
}

function TriSpotRings({ cx, cy }) {
  return (<>
    <circle cx={cx} cy={cy} r={TRI_SPOT_R} fill="var(--field)" />
    <circle cx={cx} cy={cy} r={35.57} fill="var(--cinder)" />
    <circle cx={cx} cy={cy} r={17.79} fill="var(--gold)" />
    {[4.45,8.89,17.79,26.68,35.57,44.47].map(r => (
      <circle key={r} cx={cx} cy={cy} r={r}
        fill="none" stroke="var(--nock)" strokeWidth="0.4" opacity=".55"
        strokeDasharray={r === 4.45 ? "2 2" : undefined} />
    ))}
    <line x1={cx-1.11} y1={cy}       x2={cx+1.11} y2={cy}       stroke="var(--nock)" strokeWidth=".4" opacity=".55" />
    <line x1={cx}       y1={cy-1.11} x2={cx}       y2={cy+1.11} stroke="var(--nock)" strokeWidth=".4" opacity=".55" />
  </>);
}

function NFAA5SpotRings({ cx, cy }) {
  return (<>
    <circle cx={cx} cy={cy} r={NFAA5_SPOT_R}  fill="var(--field)" />
    <circle cx={cx} cy={cy} r={21.9}  fill="var(--vellum)" />
    <circle cx={cx} cy={cy} r={44.5}  fill="none" stroke="var(--nock)" strokeWidth=".6"  opacity=".7" />
    <circle cx={cx} cy={cy} r={33.3}  fill="none" stroke="var(--nock)" strokeWidth=".5"  opacity=".5" />
    <circle cx={cx} cy={cy} r={21.9}  fill="none" stroke="var(--nock)" strokeWidth=".5"  opacity=".5" />
    <circle cx={cx} cy={cy} r={10.6}  fill="none" stroke="var(--nock)" strokeWidth=".5"  opacity=".6" />
    <line x1={cx-4.1} y1={cy-4.1} x2={cx+4.1} y2={cy+4.1} stroke="var(--field)" strokeWidth="2.3" strokeLinecap="round" />
    <line x1={cx-4.1} y1={cy+4.1} x2={cx+4.1} y2={cy-4.1} stroke="var(--field)" strokeWidth="2.3" strokeLinecap="round" />
  </>);
}

function NFAASpotRings({ cx, cy }) {
  return (<>
    <circle cx={cx} cy={cy} r={NFAA_SINGLE_OUTER_R} fill="var(--field)" />
    <circle cx={cx} cy={cy} r={19.3} fill="var(--vellum)" />
    <circle cx={cx} cy={cy} r={96.9} fill="none" stroke="var(--nock)" strokeWidth=".5" opacity=".6" />
    <circle cx={cx} cy={cy} r={77.2} fill="none" stroke="var(--nock)" strokeWidth=".5" opacity=".5" />
    <circle cx={cx} cy={cy} r={57.8} fill="none" stroke="var(--nock)" strokeWidth=".5" opacity=".5" />
    <circle cx={cx} cy={cy} r={38.4} fill="none" stroke="var(--nock)" strokeWidth=".5" opacity=".5" />
    <circle cx={cx} cy={cy} r={19.3} fill="none" stroke="var(--nock)" strokeWidth=".5" opacity=".5" />
    <circle cx={cx} cy={cy} r={9.6}  fill="none" stroke="var(--nock)" strokeWidth=".5" opacity=".6" />
    <line x1={cx-3.8} y1={cy-3.8} x2={cx+3.8} y2={cy+3.8} stroke="var(--field)" strokeWidth="2.0" strokeLinecap="round" />
    <line x1={cx-3.8} y1={cy+3.8} x2={cx+3.8} y2={cy-3.8} stroke="var(--field)" strokeWidth="2.0" strokeLinecap="round" />
  </>);
}

function NFAAFieldRings({ cx, cy }) {
  return (<>
    <circle cx={cx} cy={cy} r={WA_OUTER_R} fill="var(--ring-dark)" />
    <circle cx={cx} cy={cy} r="60"  fill="var(--vellum)" />
    <circle cx={cx} cy={cy} r="20"  fill="var(--ring-dark)" />
    {[10,20,40,60,80,100].map(r => (
      <circle key={r} cx={cx} cy={cy} r={r} fill="none" stroke="var(--nock)" strokeWidth=".5" opacity=".6" />
    ))}
    <line x1={cx-5} y1={cy}   x2={cx+5} y2={cy}   stroke="var(--vellum)" strokeWidth=".8" opacity=".85" />
    <line x1={cx}   y1={cy-5} x2={cx}   y2={cy+5} stroke="var(--vellum)" strokeWidth=".8" opacity=".85" />
  </>);
}

function NFAAHunterRings({ cx, cy }) {
  return (<>
    <circle cx={cx} cy={cy} r={WA_OUTER_R} fill="var(--ring-dark)" />
    <circle cx={cx} cy={cy} r="20"  fill="var(--vellum)" />
    {[10,20,100].map(r => (
      <circle key={r} cx={cx} cy={cy} r={r} fill="none" stroke="var(--nock)" strokeWidth=".5" opacity=".6" />
    ))}
    <circle cx={cx} cy={cy} r="60" fill="none" stroke="var(--vellum)" strokeWidth=".7" opacity=".75" />
    <line x1={cx-5} y1={cy}   x2={cx+5} y2={cy}   stroke="var(--nock)" strokeWidth=".8" opacity=".85" />
    <line x1={cx}   y1={cy-5} x2={cx}   y2={cy+5} stroke="var(--nock)" strokeWidth=".8" opacity=".85" />
  </>);
}

function AnimalRings({ cx, cy }) {
  return (<>
    <circle cx={cx} cy={cy} r={D3_OUTER_R} fill="var(--field)" />
    <circle cx={cx} cy={cy} r="50" fill="var(--cinder)" />
    <circle cx={cx} cy={cy} r="28" fill="var(--gold)" />
    <circle cx={cx} cy={cy} r="12" fill="var(--vellum)" />
    {[12,28,50,70].map(r => (
      <circle key={r} cx={cx} cy={cy} r={r} fill="none" stroke="var(--nock)" strokeWidth=".5" opacity=".55" />
    ))}
    <line x1={cx-5} y1={cy}   x2={cx+5} y2={cy}   stroke="var(--nock)" strokeWidth=".7" opacity=".55" />
    <line x1={cx}   y1={cy-5} x2={cx}   y2={cy+5} stroke="var(--nock)" strokeWidth=".7" opacity=".55" />
  </>);
}

function Animal2ArrowRings({ cx, cy }) {
  return (<>
    <circle cx={cx} cy={cy} r="50" fill="var(--cinder)" />
    <circle cx={cx} cy={cy} r="28" fill="var(--gold)" />
    <circle cx={cx} cy={cy} r="12" fill="var(--vellum)" />
    {[12,28,50].map(r => (
      <circle key={r} cx={cx} cy={cy} r={r} fill="none" stroke="var(--nock)" strokeWidth=".5" opacity=".55" />
    ))}
    <line x1={cx-5} y1={cy}   x2={cx+5} y2={cy}   stroke="var(--nock)" strokeWidth=".7" opacity=".55" />
    <line x1={cx}   y1={cy-5} x2={cx}   y2={cy+5} stroke="var(--nock)" strokeWidth=".7" opacity=".55" />
  </>);
}

function ImperialRings({ cx, cy }) {
  return (<>
    <circle cx={cx} cy={cy} r={WA_OUTER_R} fill="var(--vellum)" />
    <circle cx={cx} cy={cy} r="80"  fill="var(--ring-dark)" />
    <circle cx={cx} cy={cy} r="60"  fill="var(--field)" />
    <circle cx={cx} cy={cy} r="40"  fill="var(--cinder)" />
    <circle cx={cx} cy={cy} r="20"  fill="var(--gold)" />
    <circle cx={cx} cy={cy} r="100" {...RING_STROKE} />
    <circle cx={cx} cy={cy} r="80"  {...RING_STROKE_MED} />
    <circle cx={cx} cy={cy} r="60"  {...RING_STROKE_MED} />
    <circle cx={cx} cy={cy} r="40"  {...RING_STROKE_MED} />
    <circle cx={cx} cy={cy} r="20"  {...RING_STROKE_LIGHT} />
  </>);
}

// WA 6-ring 80 cm face ("WP-80 6 ring", compound 50 m). The inner 6 rings of
// the standard WA face — gold (10/9), red (8/7), blue (6/5) — scored X/10/9/8/
// 7/6/5. The radii are rescaled so ring 5's outer edge fills the face
// (r=WA_OUTER_R). Six equal rings of width WA_OUTER_R/6 ≈ 16.67; the X-ring is
// half of ring 10 (≈ 8.33). Mirrors the wa_6ring radius map in scoring.js.
function WA6RingRings({ cx, cy }) {
  return (<>
    {/* colour bands, outer→inner: blue (6/5), red (8/7), gold (10/9) */}
    <circle cx={cx} cy={cy} r={WA_OUTER_R}      fill="var(--field)" />
    <circle cx={cx} cy={cy} r={WA_OUTER_R * 2 / 3} fill="var(--cinder)" />
    <circle cx={cx} cy={cy} r={WA_OUTER_R / 3}     fill="var(--gold)" />
    {/* ring dividers at each 1/6 boundary (5↔6, 6↔7, 7↔8, 8↔9, 9↔10) */}
    {[1, 2, 3, 4, 5].map((i) => (
      <circle key={i} cx={cx} cy={cy} r={WA_OUTER_R * i / 6}
        fill="none" stroke="var(--nock)" strokeWidth="0.4"
        opacity={i % 3 === 0 ? 0.45 : 0.4} />
    ))}
    {/* X-ring (half of ring 10) + centre cross */}
    <circle cx={cx} cy={cy} r={WA_OUTER_R / 12} fill="none" stroke="var(--nock)" strokeWidth="0.4" strokeDasharray="2 1.5" opacity=".5" />
    <line x1={cx-1} y1={cy}   x2={cx+1} y2={cy}   stroke="var(--nock)" strokeWidth="0.4" opacity=".5" />
    <line x1={cx}   y1={cy-1} x2={cx}   y2={cy+1} stroke="var(--nock)" strokeWidth="0.4" opacity=".5" />
  </>);
}

// WA 5-ring face. The inner 5 rings of the standard WA face — gold (10/9),
// red (8/7), blue (6) — scored X/10/9/8/7/6. The face fills to where ring 6
// ends; r=1 of the face is ring 6's outer edge. Same WA colour palette.
function WA5RingRings({ cx, cy }) {
  return (<>
    {/* colour bands, outer→inner: blue (6), red (8/7), gold (10/9) */}
    <circle cx={cx} cy={cy} r={WA_OUTER_R}          fill="var(--field)" />
    <circle cx={cx} cy={cy} r={WA_OUTER_R * 0.60}   fill="var(--cinder)" />
    <circle cx={cx} cy={cy} r={WA_OUTER_R * 0.20}   fill="var(--gold)" />
    {/* ring dividers at each boundary (6↔7, 7↔8, 8↔9, 9↔10) */}
    {[0.20, 0.40, 0.60, 0.80].map((frac) => (
      <circle key={frac} cx={cx} cy={cy} r={WA_OUTER_R * frac}
        fill="none" stroke="var(--nock)" strokeWidth="0.4" opacity="0.4" />
    ))}
    {/* X-ring at r=0.10 of face */}
    <circle cx={cx} cy={cy} r={WA_OUTER_R * 0.10} fill="none" stroke="var(--nock)" strokeWidth="0.4" strokeDasharray="2 1.5" opacity=".5" />
    <line x1={cx-1} y1={cy}   x2={cx+1} y2={cy}   stroke="var(--nock)" strokeWidth="0.4" opacity=".5" />
    <line x1={cx}   y1={cy-1} x2={cx}   y2={cy+1} stroke="var(--nock)" strokeWidth="0.4" opacity=".5" />
  </>);
}

// WA Field face: 6 equal rings — gold centre 6 (r≤0.1667, with an inner spot
// X at r≤0.0833 for tiebreaks), then 5 (gold, r≤0.3333), 4 (r≤0.50), 3 (r≤0.6667),
// 2 (r≤0.8333), 1 (r≤1.00). Gold centre (6/5) over graduated outer rings.
// WAFieldSpotRings renders at a configurable radius so the same function works
// for both single-spot (r=WA_OUTER_R) and 3-spot (r=33) variants.
function WAFieldSpotRings({ cx, cy, r = WA_OUTER_R }) {
  return (<>
    {/* colour bands, outer→inner: 1 vellum, 2 nock, 3 field, 4 cinder, 5/6/X gold */}
    <circle cx={cx} cy={cy} r={r}            fill="var(--vellum)" />
    <circle cx={cx} cy={cy} r={r * 0.8333}   fill="var(--nock)" />
    <circle cx={cx} cy={cy} r={r * 0.6667}   fill="var(--field)" />
    <circle cx={cx} cy={cy} r={r * 0.5000}   fill="var(--cinder)" />
    <circle cx={cx} cy={cy} r={r * 0.3333}   fill="var(--gold)" />
    {/* ring dividers at each boundary */}
    {[0.1667, 0.3333, 0.5000, 0.6667, 0.8333].map((frac) => (
      <circle key={frac} cx={cx} cy={cy} r={r * frac}
        fill="none" stroke="var(--nock)" strokeWidth="0.4" opacity="0.4" />
    ))}
    {/* X-ring (inner spot) at r=0.0833 of face */}
    <circle cx={cx} cy={cy} r={r * 0.0833} fill="none" stroke="var(--nock)" strokeWidth="0.4" strokeDasharray="2 1.5" opacity=".5" />
    <line x1={cx-1} y1={cy}   x2={cx+1} y2={cy}   stroke="var(--nock)" strokeWidth="0.4" opacity=".5" />
    <line x1={cx}   y1={cy-1} x2={cx}   y2={cy+1} stroke="var(--nock)" strokeWidth="0.4" opacity=".5" />
  </>);
}

// Single-spot variant uses the full WA_OUTER_R (fills the 240×240 viewBox).
function WAFieldRings({ cx, cy }) {
  return <WAFieldSpotRings cx={cx} cy={cy} r={WA_OUTER_R} />;
}

function WorcesterRings({ cx, cy }) {
  return (<>
    <circle cx={cx} cy={cy} r={WA_OUTER_R} fill="var(--vellum)" />
    <circle cx={cx} cy={cy} r="80"  fill="var(--ring-dark)" />
    <circle cx={cx} cy={cy} r="60"  fill="var(--ring-dark)" />
    <circle cx={cx} cy={cy} r="40"  fill="var(--ring-dark)" />
    <circle cx={cx} cy={cy} r="20"  fill="var(--vellum)" />
    <circle cx={cx} cy={cy} r="100" {...RING_STROKE} />
    <circle cx={cx} cy={cy} r="80"  {...RING_STROKE_MED} />
    <circle cx={cx} cy={cy} r="60"  {...RING_STROKE_MED} />
    <circle cx={cx} cy={cy} r="40"  {...RING_STROKE_MED} />
    <circle cx={cx} cy={cy} r="20"  {...RING_STROKE_LIGHT} />
    <line x1={cx-5} y1={cy}   x2={cx+5} y2={cy}   stroke="var(--nock)" strokeWidth=".7" opacity=".55" />
    <line x1={cx}   y1={cy-5} x2={cx}   y2={cy+5} stroke="var(--nock)" strokeWidth=".7" opacity=".55" />
  </>);
}

// ─────────────────────────────────────────────────────────────────────────────
// 3D animal faces — deer silhouette + stacked zone-value labels. Shared by
// AnimalTarget3D (5/8/10/12), AnimalTargetIBO3D (5/8/10/11) and
// AnimalTarget3D2Arrow (8/10/11 — no body zone).
// ─────────────────────────────────────────────────────────────────────────────
const D3_LABEL = {
  fontFamily: '"Geist Mono",monospace',
  fontSize: 9,
  fontWeight: 600,
  fill: "var(--nock)",
  pointerEvents: "none",
};

function DeerSilhouette() {
  return (
    <g fill="var(--fg-low)" opacity=".22">
      <ellipse cx="120" cy="134" rx="76" ry="54" />
      <rect x="150" y="70" width="22" height="42" rx="8" />
      <ellipse cx="167" cy="60" rx="17" ry="13" />
      <ellipse cx="181" cy="48" rx="6" ry="11" />
      <path d="M172 46 L178 26 M178 26 L187 16 M178 26 L171 19" stroke="var(--fg-low)" strokeWidth="3" fill="none" strokeLinecap="round" />
    </g>
  );
}

// Rows are given top-down; `dim` labels sit over dark rings and use --vellum.
function D3Labels({ rows }) {
  return rows.map(({ y, text, dim }) => (
    <text key={y} x="120" y={y} textAnchor="middle"
      style={dim ? { ...D3_LABEL, fill: "var(--vellum)" } : D3_LABEL}>
      {text}
    </text>
  ));
}

const MAT = "var(--surface-2)";

// ─────────────────────────────────────────────────────────────────────────────
// FACE_LAYERS — face id → background layers. The default (unknown face id) is
// the WA 10-ring face, matching the pre-#2063 `default:` branches.
// ─────────────────────────────────────────────────────────────────────────────
export const FACE_LAYERS = {
  wa40: {
    mat: true,
    rings: () => <WARings cx={120} cy={120} />,
  },
  vegas: {
    mat: true,
    rings: () => SPOT_CENTERS.map((c) => <VegasSpotRings key={c.y} cx={120} cy={c.y} />),
  },
  "3spot-tri": {
    mat: true,
    rings: () => TRI_CENTERS.map((c, i) => <TriSpotRings key={i} cx={c.x} cy={c.y} />),
  },
  nfaa5spot: {
    background: "var(--ring-dark)",
    previewBg: "var(--ring-dark)",
    rings: () => NFAA5_CENTERS.map((c, i) => <NFAA5SpotRings key={i} cx={c.x} cy={c.y} />),
  },
  nfaaspot: {
    background: "var(--ring-dark)",
    previewBg: "var(--ring-dark)",
    rings: () => <NFAASpotRings cx={120} cy={120} />,
  },
  nfaafield: {
    mat: true,
    rings: () => <NFAAFieldRings cx={120} cy={120} />,
  },
  nfaahunter: {
    mat: true,
    rings: () => <NFAAHunterRings cx={120} cy={120} />,
  },
  worcester: {
    mat: true,
    rings: () => <WorcesterRings cx={120} cy={120} />,
  },
  imperial5zone: {
    mat: true,
    rings: () => <ImperialRings cx={120} cy={120} />,
  },
  // wa5zone shares Imperial's geometry — before #1436 it fell through to the
  // plain WA 10-ring face and mis-rendered.
  wa5zone: {
    mat: true,
    rings: () => <ImperialRings cx={120} cy={120} />,
  },
  wa6ring: {
    mat: true,
    rings: () => <WA6RingRings cx={120} cy={120} />,
  },
  wa5ring80: {
    mat: true,
    rings: () => <WA5RingRings cx={120} cy={120} />,
  },
  wa5ring122: {
    mat: true,
    rings: () => <WA5RingRings cx={120} cy={120} />,
  },
  wafield40: {
    mat: true,
    rings: () => <WAFieldRings cx={120} cy={120} />,
  },
  wafield60: {
    mat: true,
    rings: () => <WAFieldRings cx={120} cy={120} />,
  },
  wafield80: {
    mat: true,
    rings: () => <WAFieldRings cx={120} cy={120} />,
  },
  wafield3spot: {
    mat: true,
    rings: () => WAFIELD3_SPOT_CENTERS.map((c, i) => (
      <WAFieldSpotRings key={i} cx={c.x} cy={c.y} r={WAFIELD3_SPOT_R} />
    )),
  },
  "3d": {
    background: MAT,
    decor: () => <DeerSilhouette />,
    rings: () => <AnimalRings cx={D3_VITAL.x} cy={D3_VITAL.y} />,
    labels: () => <D3Labels rows={[
      { y: 73,  text: "5",  dim: true },
      { y: 92,  text: "8",  dim: true },
      { y: 111, text: "10" },
      { y: 133, text: "12" },
    ]} />,
  },
  ibo3d: {
    background: MAT,
    decor: () => <DeerSilhouette />,
    rings: () => <AnimalRings cx={D3_VITAL.x} cy={D3_VITAL.y} />,
    labels: () => <D3Labels rows={[
      { y: 73,  text: "5",  dim: true },
      { y: 92,  text: "8",  dim: true },
      { y: 111, text: "10" },
      { y: 133, text: "11" },
    ]} />,
  },
  "3d-2arrow": {
    background: MAT,
    decor: () => <DeerSilhouette />,
    rings: () => <Animal2ArrowRings cx={D3_VITAL.x} cy={D3_VITAL.y} />,
    labels: () => <D3Labels rows={[
      { y: 92,  text: "8",  dim: true },
      { y: 111, text: "10" },
      { y: 133, text: "11" },
    ]} />,
  },
};

/** Layers for a face id; unknown ids fall back to the WA 10-ring face. */
export function faceLayers(face) {
  return FACE_LAYERS[face] || FACE_LAYERS.wa40;
}

/**
 * Face-accurate ring geometry for the magnified drag preview — the SAME
 * `rings` layer the canvas draws, so preview and face can no longer drift.
 * Decorative art and zone labels are deliberately omitted; faces that get
 * their backdrop from the SVG `background` need an explicit rect here.
 */
export function previewRings(face) {
  const layers = faceLayers(face);
  return (
    <>
      {layers.previewBg && <rect x="0" y="0" width="240" height="240" fill={layers.previewBg} />}
      {layers.rings()}
    </>
  );
}
